<script setup>
import { usePage } from '@inertiajs/vue3';
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import InputField from '@js/components/fields/InputField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSuccess } from '@js/utils/toast';
import { reactive, ref, watch } from 'vue';
import { number, object } from 'yup';

const page = usePage();

const configurationKey = page.props.objectiveTimesConfigurationKey;

const fields = page.props.objectiveTimeFields ?? [];

const groupedFields = fields.reduce((groups, field) => {
    let group = groups.find((item) => item.name === field.group);

    if (!group) {
        group = { name: field.group, fields: [] };
        groups.push(group);
    }

    group.fields.push(field);

    return groups;
}, []);

const { callback } = defineProps({
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const isOpen = ref(false);

const loading = ref(true);

const isSubmitting = ref(false);

const storedData = ref({});

const form = reactive(
    Object.fromEntries(fields.map((field) => [field.key, field.default])),
);

const validationSchema = object(
    Object.fromEntries(
        fields.map((field) => [
            field.key,
            number()
                .typeError('Ce champ doit être un nombre.')
                .min(0, 'Ce champ doit être positif.')
                .required('Ce champ est requis.'),
        ]),
    ),
);

function updateData() {
    loading.value = true;

    axios
        .get(route('front.getConfiguration', { key: configurationKey }))
        .then((response) => {
            storedData.value = response.data.data ?? {};
        })
        .catch(() => {
            storedData.value = {};
        })
        .finally(() => {
            fields.forEach((field) => {
                form[field.key] = storedData.value[field.key] ?? field.default;
            });

            loading.value = false;
        });
}

function saveAction() {
    isSubmitting.value = true;

    axios
        .post(route('front.postConfiguration', { key: configurationKey }), {
            data: {
                ...storedData.value,
                ...form,
            },
        })
        .then(() => {
            toastSuccess('Configuration enregistrée.');

            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}

watch(isOpen, (value) => {
    if (value) {
        updateData();
    }
});
</script>

<template>
    <DialogModal v-model="isOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Configuration du temps par objectif </template>

        <template #full-content>
            <FormRoot
                v-slot="{ errors }"
                :loading="loading"
                :validation-schema="validationSchema"
                :submit="saveAction"
            >
                <FormColumn>
                    <FormContent
                        v-for="group in groupedFields"
                        :key="group.name"
                    >
                        <FormTitle>{{ group.name }}</FormTitle>

                        <FormGrid :columns="2">
                            <InputField
                                v-for="field in group.fields"
                                :key="field.key"
                                v-model="form[field.key]"
                                :label="`${field.label} (${field.suffix})`"
                                :field-name="field.key"
                                :field-id="field.key"
                                type="number"
                                min="0"
                                step="1"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogModal>
</template>
